import { useCallback, useEffect, useState } from "react";
import { Input, Select, Spin, Table, Tag } from "antd";
import type { TableColumnsType } from "antd";
import { AdminListShell } from "../components/admin/AdminListShell";
import { fetchAdminSubscriptionsPage } from "../api/client";
import type { AdminSubscriptionRow } from "../api/types";
import { fmtDateTime } from "../lib/formatAdmin";

const STATUS_OPTS = [
  { value: "", label: "全部状态" },
  { value: "active", label: "active" },
  { value: "canceled", label: "canceled" },
  { value: "expired", label: "expired" },
];

export function SubscriptionsPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState("");
  const [userId, setUserId] = useState("");
  const [items, setItems] = useState<AdminSubscriptionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<"forbidden" | "error" | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const r = await fetchAdminSubscriptionsPage({
      page,
      page_size: pageSize,
      status: status || undefined,
      user_id: userId || undefined,
    });
    if (r === "forbidden") setErr("forbidden");
    else if (r === "error") setErr("error");
    else {
      setItems(r.items);
      setTotal(r.total);
    }
    setLoading(false);
  }, [page, pageSize, status, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [status, userId]);

  const columns: TableColumnsType<AdminSubscriptionRow> = [
    {
      title: "用户",
      key: "u",
      width: 220,
      ellipsis: true,
      render: (_, row) => (
        <div>
          <div className="admin-ledger-email">{row.user_email ?? "—"}</div>
          <div className="admin-ledger-mono">{row.user_id.slice(0, 13)}…</div>
        </div>
      ),
    },
    {
      title: "套餐",
      key: "p",
      width: 200,
      render: (_, row) => (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{row.plan_name ?? "—"}</div>
          <div className="admin-ledger-mono" style={{ opacity: 0.75 }}>
            {row.plan_code ?? "—"}
          </div>
        </div>
      ),
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 90,
      render: (s: string) => <Tag>{s}</Tag>,
    },
    { title: "开始", dataIndex: "starts_at", width: 158, render: fmtDateTime },
    { title: "结束", dataIndex: "ends_at", width: 158, render: fmtDateTime },
    {
      title: "自动续费",
      dataIndex: "auto_renew",
      width: 88,
      render: (v: boolean) => (v ? "是" : "否"),
    },
    {
      title: "渠道",
      dataIndex: "provider",
      width: 120,
      ellipsis: true,
      render: (v: string | null) => v ?? "—",
    },
  ];

  return (
    <AdminListShell
      title="订阅"
      description="用户套餐订阅与周期、支付渠道关联（只读）。"
      err={err}
      errorBanner
      toolbar={
        <div className="admin-ledger-toolbar" style={{ flexWrap: "wrap" }}>
          <Select
            value={status || ""}
            onChange={(v) => setStatus(v)}
            options={STATUS_OPTS}
            style={{ minWidth: 120 }}
            variant="borderless"
          />
          <div className="admin-ledger-toolbar-divider" />
          <Input
            placeholder="用户 UUID"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            variant="borderless"
            style={{ width: 220 }}
            allowClear
          />
        </div>
      }
      table={(bodyY) => (
        <div className="admin-ledger-table-wrap admin-ledger-table-wrap--flex">
          <Spin spinning={loading}>
            <Table<AdminSubscriptionRow>
              rowKey="id"
              className="admin-ledger-table"
              columns={columns}
              dataSource={items}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50],
                showTotal: (t) => `共 ${t} 条`,
                onChange: (p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                },
              }}
              scroll={{ x: 1200, y: bodyY }}
              locale={{ emptyText: "暂无订阅" }}
            />
          </Spin>
        </div>
      )}
    />
  );
}
